import { useState } from "react";
import { Check, Eye, Mail, Send, Trash2, X } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { api, fetchCollection, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const ROLE_LABEL = { admin: "Administrador", editor: "Editor", colaborador: "Colaborador", suscriptor: "Suscriptor" };

function Flag({ checked, onChange, label, testId }) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2 text-xs text-slate-300">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} className="h-3.5 w-3.5 accent-[#ff6b00]" data-testid={testId} />
      {label}
    </label>
  );
}

function PendingCard({ item, onDone }) {
  const [flags, setFlags] = useState({ can_publish_posters: item.requested_posters, can_publish_chronicles: item.requested_chronicles, wants_newsletter: item.wants_newsletter });
  const approve = useMutation({
    mutationFn: async () => (await api.post(`/admin/users/${item.id}/approve`, flags)).data,
    onSuccess: () => { toast.success(`${item.name} aprobado`); onDone(); },
    onError: (error) => toast.error(formatApiError(error)),
  });
  const reject = useMutation({
    mutationFn: async () => (await api.post(`/admin/users/${item.id}/reject`)).data,
    onSuccess: () => { toast.success(`${item.name} rechazado`); onDone(); },
    onError: (error) => toast.error(formatApiError(error)),
  });
  return (
    <article className="border border-brass/40 bg-brass/5 p-4" data-testid={`admin-pending-user-${item.id}`}>
      <p className="font-bold text-slate-100">{item.name} <span className="ml-2 border border-brass/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-brass">Pendiente</span></p>
      <p className="text-xs text-slate-400">{item.email}{item.club ? ` · ${item.club}` : ""}</p>
      <p className="mt-2 text-xs text-slate-500">Solicita: {[item.requested_posters && "publicar carteles", item.requested_chronicles && "publicar crónicas", item.wants_newsletter && "boletín semanal"].filter(Boolean).join(", ") || "nada marcado"}</p>
      <div className="mt-3 flex flex-wrap gap-4">
        <Flag checked={flags.can_publish_posters} onChange={(value) => setFlags({ ...flags, can_publish_posters: value })} label="Puede publicar carteles" testId={`admin-pending-posters-${item.id}`} />
        <Flag checked={flags.can_publish_chronicles} onChange={(value) => setFlags({ ...flags, can_publish_chronicles: value })} label="Puede publicar crónicas" testId={`admin-pending-chronicles-${item.id}`} />
        <Flag checked={flags.wants_newsletter} onChange={(value) => setFlags({ ...flags, wants_newsletter: value })} label="Recibe el boletín" testId={`admin-pending-newsletter-${item.id}`} />
      </div>
      <div className="mt-4 flex gap-2">
        <Button type="button" onClick={() => approve.mutate()} disabled={approve.isPending} className="rounded-none bg-clay font-mono text-[10px] uppercase tracking-[0.18em] text-charcoal hover:bg-brass" data-testid={`admin-approve-user-${item.id}`}><Check className="h-3 w-3" /> Aprobar</Button>
        <Button type="button" variant="outline" onClick={() => reject.mutate()} disabled={reject.isPending} className="rounded-none border-red-500/25 bg-red-500/5 font-mono text-[10px] uppercase tracking-[0.18em] text-red-200 hover:bg-red-500/20" data-testid={`admin-reject-user-${item.id}`}><X className="h-3 w-3" /> Rechazar</Button>
      </div>
    </article>
  );
}

function UserCard({ item, me, onDone }) {
  const permissions = useMutation({
    mutationFn: async (payload) => (await api.put(`/admin/users/${item.id}/permissions`, payload)).data,
    onSuccess: () => { toast.success("Permisos actualizados"); onDone(); },
    onError: (error) => toast.error(formatApiError(error)),
  });
  const remove = useMutation({
    mutationFn: async () => (await api.delete(`/admin/users/${item.id}`)).data,
    onSuccess: () => { toast.success("Usuario eliminado"); onDone(); },
    onError: (error) => toast.error(formatApiError(error)),
  });
  const staff = item.role === "admin" || item.role === "editor";
  const set = (patch) => {
    const next = { can_publish_posters: item.can_publish_posters, can_publish_chronicles: item.can_publish_chronicles, wants_newsletter: item.wants_newsletter, role: item.role, ...patch };
    if (!staff) next.role = next.can_publish_posters || next.can_publish_chronicles ? "colaborador" : "suscriptor";
    permissions.mutate(next);
  };
  return (
    <article className={`border p-4 ${item.status === "rejected" ? "border-red-500/20 bg-red-500/5 opacity-70" : "border-white/10 bg-white/[0.03]"}`} data-testid={`admin-user-item-${item.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="font-bold text-slate-100">{item.name} <span className="ml-2 border border-clay/40 bg-clay/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-clay">{ROLE_LABEL[item.role] || item.role}</span>{item.status === "rejected" ? <span className="ml-2 border border-red-500/30 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-red-200">Rechazado</span> : null}</p>
          <p className="truncate text-xs text-slate-400">{item.email}{item.club ? ` · ${item.club}` : ""}</p>
        </div>
        {item.id !== me?.id ? <Button type="button" variant="outline" onClick={() => window.confirm(`¿Eliminar a ${item.name}?`) && remove.mutate()} className="rounded-none border-red-500/25 bg-red-500/5 font-mono text-[10px] uppercase tracking-[0.18em] text-red-200 hover:bg-red-500/20" data-testid={`admin-user-delete-${item.id}`}><Trash2 className="h-3 w-3" /></Button> : null}
      </div>
      {item.status !== "rejected" ? (
        <div className="mt-3 flex flex-wrap gap-4">
          {!staff ? <Flag checked={item.can_publish_posters} onChange={(value) => set({ can_publish_posters: value })} label="Carteles" testId={`admin-user-posters-${item.id}`} /> : null}
          {!staff ? <Flag checked={item.can_publish_chronicles} onChange={(value) => set({ can_publish_chronicles: value })} label="Crónicas" testId={`admin-user-chronicles-${item.id}`} /> : null}
          <Flag checked={item.wants_newsletter} onChange={(value) => set({ wants_newsletter: value })} label="Boletín" testId={`admin-user-newsletter-${item.id}`} />
        </div>
      ) : null}
    </article>
  );
}

export function UsersList() {
  const { user: me } = useAuth();
  const queryClient = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["admin-users"], queryFn: () => fetchCollection("/admin/users") });
  const refresh = () => { queryClient.invalidateQueries({ queryKey: ["admin-users"] }); queryClient.invalidateQueries({ queryKey: ["newsletter-status"] }); };
  const pending = data.filter((item) => item.status === "pending");
  const others = data.filter((item) => item.status !== "pending");
  return (
    <div className="grid gap-6" data-testid="admin-users-list">
      {pending.length ? (
        <div className="grid gap-3" data-testid="admin-pending-users">
          <p className="eyebrow">Solicitudes pendientes ({pending.length})</p>
          {pending.map((item) => <PendingCard key={item.id} item={item} onDone={refresh} />)}
        </div>
      ) : <p className="text-xs text-slate-500" data-testid="admin-no-pending-users">No hay solicitudes de registro pendientes.</p>}
      <div className="grid gap-3">
        <p className="eyebrow">Usuarios ({others.length})</p>
        {others.map((item) => <UserCard key={item.id} item={item} me={me} onDone={refresh} />)}
      </div>
    </div>
  );
}

export function NewsletterCard() {
  const queryClient = useQueryClient();
  const { data } = useQuery({ queryKey: ["newsletter-status"], queryFn: () => fetchCollection("/admin/newsletter/status") });
  const test = useMutation({
    mutationFn: async () => (await api.post("/admin/newsletter/test")).data,
    onSuccess: (result) => toast.success(`Correo de prueba enviado a ${result.sent_to}`),
    onError: (error) => toast.error(formatApiError(error)),
  });
  const send = useMutation({
    mutationFn: async () => (await api.post("/admin/newsletter/send")).data,
    onSuccess: (result) => { toast.success(`Boletín enviado a ${result.sent} de ${result.recipients} suscriptores`); queryClient.invalidateQueries({ queryKey: ["newsletter-status"] }); },
    onError: (error) => toast.error(formatApiError(error)),
  });
  const preview = `${process.env.REACT_APP_BACKEND_URL}/api/admin/newsletter/preview`;
  return (
    <div className="border border-white/10 bg-white/[0.03] p-5" data-testid="admin-newsletter-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="eyebrow">Boletín semanal</p>
          <p className="mt-2 text-sm text-slate-300" data-testid="admin-newsletter-summary">{data?.subscribers ?? 0} suscriptores · {data?.schedule || "Domingos a las 16:00"}</p>
          <p className="mt-1 text-xs text-slate-500" data-testid="admin-newsletter-email-state">
            {data?.email_enabled ? `Correo configurado (${data.sender})` : "Correo no configurado: el boletín no se enviará hasta añadir la cuenta de Gmail."}
            {data?.last_run ? ` · Último envío: ${new Date(data.last_run.finished_at).toLocaleString("es-ES")} (${data.last_run.sent} enviados${data.last_run.failed ? `, ${data.last_run.failed} fallidos` : ""})` : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline" className="rounded-none border-white/15 bg-transparent font-mono text-[10px] uppercase tracking-[0.18em]"><a href={preview} target="_blank" rel="noreferrer" data-testid="admin-newsletter-preview-link"><Eye className="h-3 w-3" /> Vista previa</a></Button>
          <Button type="button" variant="outline" onClick={() => test.mutate()} disabled={test.isPending || !data?.email_enabled} className="rounded-none border-white/15 bg-transparent font-mono text-[10px] uppercase tracking-[0.18em]" data-testid="admin-newsletter-test-button"><Mail className="h-3 w-3" /> {test.isPending ? "Enviando..." : "Enviarme una prueba"}</Button>
          <Button type="button" onClick={() => window.confirm("¿Enviar ahora el boletín a todos los suscriptores?") && send.mutate()} disabled={send.isPending || !data?.email_enabled} className="rounded-none bg-clay font-mono text-[10px] uppercase tracking-[0.18em] text-charcoal hover:bg-brass" data-testid="admin-newsletter-send-button"><Send className="h-3 w-3" /> {send.isPending ? "Enviando..." : "Enviar ahora"}</Button>
        </div>
      </div>
    </div>
  );
}
